import { useState } from "react";
import BackgroundLayer from "@/components/BackgroundLayer";
import RadioGroup from "@/components/RadioGroup";
import axios from "axios";
import { Check, Play } from "lucide-react";
import { openCookiePrefs } from "@/components/CookieBanner";

const MODULE_OPTIONS = [
  { value: "decode", label: "Decode Your Portfolio — Understand what you own and why it moves" },
  { value: "stress", label: "Stress-Test Simulator — Run your actual allocation through historical crises" },
  { value: "bias", label: "Behavioral Bias Profile — Build your full behavioral map across documented biases" },
];

const YOUTUBE_ID = "8YxBxScihPE";

export default function SignUpPage({ sessionData, backendUrl, stageIndex, onBack, completed, setCompleted }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [noted, setNoted] = useState(false);
  const [module, setModule] = useState(null);
  const [comment, setComment] = useState("");
  const [prefSaved, setPrefSaved] = useState(() => sessionStorage.getItem("pord_pref_saved") === "true");
  const [showVideo, setShowVideo] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // If already completed (came back from exhale after finishing), show thank you
  if (completed) {
    return (
      <div className="page-wrapper" data-testid="signup-page">
        <BackgroundLayer image="/images/page7_bg.jpg" />
        <div className="content-layer" style={{ minHeight: "100vh", minHeight: "100dvh", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div className="page-content" style={{ textAlign: "center", paddingTop: 80 }}>
            <p className="section-heading animate-in" style={{ color: "var(--accent-teal)", fontSize: "clamp(1.4rem, 4vw, 1.8rem)" }}>
              Thank you. See you soon.
            </p>
            <div style={{ marginTop: 32 }} className="animate-in animate-in-d2">
              {onBack && <button className="nav-back" onClick={onBack} data-testid="back-to-exhale-btn">Back to Exhale</button>}
            </div>
          </div>
          <footer style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "20px 24px 60px", textAlign: "center" }}>
            <div style={{ display: "flex", justifyContent: "center", gap: 16, flexWrap: "wrap" }}>
              <a href="/privacy" className="footnote-text ghost-link">Privacy</a>
              <a href="/do-not-sell" className="footnote-text ghost-link">Do Not Sell</a>
              <button className="footnote-text ghost-link" onClick={openCookiePrefs} style={{ border: "none", padding: 0, cursor: "pointer" }}>Cookie</button>
            </div>
          </footer>
        </div>
      </div>
    );
  }

  const handleSavePreference = async () => {
    if (!module) return;
    setLoading(true);
    try {
      await axios.post(`${backendUrl}/api/module-preference`, { module });
      if (comment.trim()) {
        await axios.post(`${backendUrl}/api/comment`, { comment: comment.trim(), module });
      }
      setPrefSaved(true);
      sessionStorage.setItem("pord_pref_saved", "true");
    } catch (e) {}
    setLoading(false);
  };

  const handleSignup = async () => {
    if (!name.trim() || !email.trim() || !noted) return;
    setLoading(true);
    setError("");
    try {
      await axios.post(`${backendUrl}/api/signup`, {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        agree_privacy: noted,
      });
      // Link module preference to email
      if (module) {
        await axios.post(`${backendUrl}/api/module-preference`, {
          module,
          email: email.trim().toLowerCase(),
        });
      }
      if (comment.trim()) {
        await axios.post(`${backendUrl}/api/comment`, {
          comment: comment.trim(),
          module,
          email: email.trim().toLowerCase(),
        });
      }
      setCompleted(true);
      // Track completed signup
      fetch(`${backendUrl}/api/track`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ event: "completed_signup", session_id: sessionStorage.getItem("pord_session_id") }),
      }).catch(() => {});
    } catch (e) {
      setError("Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  return (
    <div className="page-wrapper" data-testid="signup-page">
      <BackgroundLayer image="/images/page7_bg.jpg" />
      <div className="content-layer" style={{ minHeight: "100vh", minHeight: "100dvh" }}>
        <div className="page-content" style={{ paddingTop: 60, paddingBottom: 80 }}>
          <div className="section-label animate-in" style={{ marginBottom: 12 }}>06: Early Access Sign Up</div>
          <div className="animate-in animate-in-d1" style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 28 }}>
            <button className="listen-btn" onClick={() => setShowVideo(true)} data-testid="video-btn">
              <Play size={14} />
              <span>Video</span>
            </button>
            {onBack && <button className="nav-back" onClick={onBack} data-testid="back-to-exhale-btn">Back to Exhale</button>}
          </div>

          {/* Module Preference — shown until saved */}
          {!prefSaved && (
            <div className="animate-in animate-in-d2" style={{ marginBottom: 12 }}>
              <p className="standout-line" style={{ marginBottom: 4 }}>
                Quick question before sign-up:
              </p>
              <p className="body-text" style={{ marginBottom: 16 }}>
                Which module would you want Pord. to build first?
              </p>

              <RadioGroup name="module" options={MODULE_OPTIONS} selected={module} onSelect={setModule} />

              {/* Comment box */}
              {module && (
                <div style={{ marginTop: 20 }}>
                  <textarea
                    className="form-input"
                    placeholder="Any thoughts or comments? (optional)"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    data-testid="comment-input"
                    rows={3}
                    style={{ resize: "vertical", borderBottom: "none", border: "1px solid rgba(184,212,208,0.15)", borderRadius: 8, padding: "12px 14px", lineHeight: 1.6 }}
                  />
                </div>
              )}

              {!module && (
                <p style={{ color: "rgba(184,212,208,0.3)", fontSize: "0.78rem", letterSpacing: "0.06em", marginTop: 12 }}>
                  Select a module to continue to sign-up
                </p>
              )}

              {module && (
                <button
                  className="cta-pill"
                  onClick={handleSavePreference}
                  disabled={loading}
                  data-testid="save-preference-btn"
                  style={{ marginTop: 16 }}
                >
                  {loading ? "..." : "Save your preference"}
                </button>
              )}
            </div>
          )}

          {/* Sign-up section — appears after preference is saved */}
          {prefSaved && (
            <div className="animate-in">
              <p className="standout-line" style={{ marginBottom: 20 }}>
                One last bit: Sign-Up email
              </p>

              {/* Privacy bullets — just 2 lines + link, larger & brighter */}
              <div style={{ marginBottom: 28 }}>
                <p style={{ color: "rgba(220,224,218,0.8)", fontSize: "1rem", lineHeight: 1.75, marginBottom: 8 }}>
                  • Your email will be used only for Pord. development communications and early access updates
                </p>
                <p style={{ color: "rgba(220,224,218,0.8)", fontSize: "1rem", lineHeight: 1.75, marginBottom: 8 }}>
                  • You can request deletion at any time: info@clarityjournalbyenquips.com
                </p>
                <p style={{ marginTop: 12 }}>
                  <a href="/privacy" className="ghost-link" style={{ textDecoration: "underline", color: "rgba(184,212,208,0.6)", fontSize: "0.95rem" }}>Privacy Policy</a>
                </p>
              </div>

              {/* Noted checkbox */}
              <div
                className="checkbox-wrapper"
                onClick={() => setNoted(!noted)}
                data-testid="agree-checkbox"
                style={{ marginBottom: 24 }}
              >
                <div className={`checkbox-box ${noted ? "checked" : ""}`}>
                  {noted && <Check size={12} color="var(--cta-text)" />}
                </div>
                <span className="checkbox-label">Noted</span>
              </div>

              {/* Name + Email */}
              <div style={{ display: "flex", flexDirection: "column", gap: 20, marginBottom: 24 }}>
                <input
                  className="form-input"
                  placeholder="Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="signup-name-input"
                />
                <input
                  className="form-input"
                  placeholder="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="signup-email-input"
                />
              </div>

              {error && <p style={{ color: "#e87c7c", fontSize: "0.85rem", marginBottom: 12 }}>{error}</p>}

              {noted && (
                <button
                  className="cta-pill cta-pill-filled"
                  onClick={handleSignup}
                  disabled={!name.trim() || !email.trim() || loading}
                  data-testid="signup-submit-btn"
                  style={{ animation: "fadeInUp 0.4s cubic-bezier(0.16,1,0.3,1) both" }}
                >
                  {loading ? "..." : "Get Early Access"}
                </button>
              )}
            </div>
          )}
        </div>

        <footer style={{ padding: "20px 24px 60px", textAlign: "center" }}>
          <div style={{ display: "flex", justifyContent: "center", gap: 16, flexWrap: "wrap" }}>
            <a href="/privacy" className="footnote-text ghost-link">Privacy</a>
            <a href="/do-not-sell" className="footnote-text ghost-link">Do Not Sell</a>
            <button className="footnote-text ghost-link" onClick={openCookiePrefs} style={{ border: "none", padding: 0, cursor: "pointer" }}>Cookie</button>
          </div>
        </footer>
      </div>

      {/* YouTube Video popup */}
      {showVideo && (
        <div className="popup-overlay" onClick={() => setShowVideo(false)} data-testid="video-popup">
          <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 400, borderRadius: 16, overflow: "hidden", background: "var(--bg-surface)", border: "1px solid rgba(184,212,208,0.1)", animation: "slideUp 0.4s cubic-bezier(0.16,1,0.3,1)" }}>
            <div style={{ position: "relative", width: "100%", paddingBottom: "177.78%" }}>
              <iframe
                src={`https://www.youtube.com/embed/${YOUTUBE_ID}?autoplay=1&rel=0&modestbranding=1`}
                title="Pord. Video"
                allow="autoplay; encrypted-media"
                allowFullScreen
                style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: "none" }}
              />
            </div>
            <div style={{ padding: "16px 20px", textAlign: "center" }}>
              <button className="ghost-link" onClick={() => setShowVideo(false)} data-testid="close-video-btn">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
